<!-- FILE: src/views/EvaluationPage.vue -->
<template>
    <div class="container-fluid my-4">
        <!-- Page Header -->
        <header class="d-flex align-items-center pb-3 mb-4 border-bottom">
            <a href="#" class="d-flex align-items-center text-dark text-decoration-none">
                <i class="fas fa-clipboard-check fa-2x me-3 text-success"></i>
                <span class="fs-4">لوحة تحكيم ورصد الدرجات</span>
            </a>
        </header>

        <!-- Loading and Error State -->
        <div v-if="isLoading" class="text-center my-5">
            <div class="spinner-border text-success" role="status"></div>
            <p class="mt-2">جاري تحميل قائمة المتسابقين...</p>
        </div>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Evaluation Table -->
        <div v-if="!isLoading && !error" class="card shadow-sm">
            <div class="card-header bg-light">
                <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0"><i class="fas fa-edit me-2"></i>تقييم المتسابقين</h5>
                    <div class="w-50">
                        <input type="text" v-model="searchTerm" class="form-control" placeholder="ابحث عن متسابق...">
                    </div>
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <table class="table table-bordered table-hover align-middle">
                        <thead class="table-dark text-center">
                            <tr>
                                <th rowspan="2">الاسم</th>
                                <th colspan="3">معايير التقييم</th>
                                <th rowspan="2">المجموع (100)</th>
                                <th rowspan="2">إجراء</th>
                            </tr>
                            <tr>
                                <th>الحفظ (50)</th>
                                <th>أحكام التجويد (30)</th>
                                <th>جمال الصوت (20)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="filteredContestants.length === 0">
                                <td colspan="6" class="text-center">لا توجد نتائج مطابقة.</td>
                            </tr>
                            <tr v-for="c in filteredContestants" :key="c.id">
                                <td>{{ c.name }}</td>
                                <td><input type="number" class="form-control" v-model.number="c.hifz_score" min="0" max="50"></td>
                                <td><input type="number" class="form-control" v-model.number="c.tajweed_score" min="0" max="30"></td>
                                <td><input type="number" class="form-control" v-model.number="c.performance_score" min="0" max="20"></td>
                                <td class="text-center total-score">{{ calculateTotal(c) }}</td>
                                <td class="text-center">
                                    <button class="btn btn-sm btn-primary" @click="saveScores(c)" :disabled="c.isSaving">
                                        <span v-if="c.isSaving" class="spinner-border spinner-border-sm" role="status"></span>
                                        <i v-else class="fas fa-save me-1"></i>
                                        {{ c.isSaving ? 'جاري الحفظ...' : 'حفظ' }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Toast Notification -->
        <div class="toast-container">
            <div id="saveToast" class="toast align-items-center text-white bg-success border-0" role="alert" aria-live="assertive" aria-atomic="true">
                <div class="d-flex">
                    <div class="toast-body">
                        {{ toastMessage }}
                    </div>
                    <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../api/axios';

export default {
    name: 'EvaluationPage',
    data() {
        return {
            isLoading: true,
            error: null,
            contestants: [],
            searchTerm: '',
            toastInstance: null,
            toastMessage: '',
        };
    },
    computed: {
        filteredContestants() {
            if (!this.searchTerm) {
                return this.contestants;
            }
            const term = this.searchTerm.toLowerCase().trim();
            return this.contestants.filter(c => c.name && c.name.toLowerCase().includes(term));
        }
    },
    methods: {
        async fetchContestants() {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await api.get('/evaluation/contestants');
                // إضافة خاصية isSaving لكل متسابق لتتبع حالة الحفظ
                this.contestants = response.data.map(c => ({
                    ...c,
                    // تحويل null إلى 0 للعرض الصحيح في حقول الإدخال
                    hifz_score: c.hifz_score || 0,
                    tajweed_score: c.tajweed_score || 0,
                    performance_score: c.performance_score || 0,
                    isSaving: false
                }));
            } catch (err) {
                this.error = 'فشل في تحميل قائمة المتسابقين.';
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        },
        calculateTotal(contestant) {
            return (contestant.hifz_score || 0) + (contestant.tajweed_score || 0) + (contestant.performance_score || 0);
        },
        async saveScores(contestant) {
            this.$set(contestant, 'isSaving', true); // تفعيل مؤشر الحفظ

            const scores = {
                hifz_score: contestant.hifz_score,
                tajweed_score: contestant.tajweed_score,
                performance_score: contestant.performance_score,
            };

            try {
                const response = await api.put(`/evaluation/contestants/${contestant.id}/scores`, scores);
                // تحديث البيانات في القائمة بالبيانات الجديدة من الخادم
                Object.assign(contestant, response.data.contestant);
                this.showToast('تم حفظ الدرجات بنجاح!');
            } catch (err) {
                this.showToast('فشل حفظ الدرجات. تحقق من المدخلات.', 'bg-danger');
                console.error(err.response.data);
            } finally {
                this.$set(contestant, 'isSaving', false); // إيقاف مؤشر الحفظ
            }
        },
        showToast(message, bgClass = 'bg-success') {
            this.toastMessage = message;
            const toastEl = document.getElementById('saveToast');
            toastEl.className = `toast align-items-center text-white ${bgClass} border-0`; // تحديث لون الخلفية
            if (this.toastInstance) {
                this.toastInstance.show();
            }
        }
    },
    mounted() {
        // تهيئة الـ Toast مرة واحدة عند تحميل المكون
        const toastEl = document.getElementById('saveToast');
        if (toastEl) {
            // eslint-disable-next-line no-undef
            this.toastInstance = new bootstrap.Toast(toastEl);
        }
    },
    created() {
        this.fetchContestants();
    }
};
</script>

<style scoped>
    .total-score {
        font-weight: bold;
        font-size: 1.1rem;
        background-color: #e9f5ea;
    }
    .table input[type="number"] {
        max-width: 90px;
        text-align: center;
        border: 1px solid #ddd;
        border-radius: 4px;
        transition: border-color 0.2s;
    }
    .table input[type="number"]:focus {
        border-color: #28a745;
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
    .toast-container {
        position: fixed;
        bottom: 1rem;
        left: 1rem;
        z-index: 1055;
    }
</style>